import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { selectFeatureFlag } from "shared/reducers/sysReducer";
import {
  selectSortedCaseDetailSlides,
  selectSortedCaseDetailSpecimens,
} from "shared/selectors/selectSortedCaseDetail";

import { useAppSelector } from "store/hooks";

export type ViewerItem = {
  id: number;
  type: "slides" | "documents" | "specimens";
  specimenId?: number;
};

const useViewerItems = (): ViewerItem[] => {
  const caseIdParam = parseInt(useParams().case_id || "");
  const caseDetailId = useAppSelector(({ caseDetail }) => caseDetail?.id);
  const sortedSpecimens = useAppSelector(selectSortedCaseDetailSpecimens);
  const sortedSlides = useAppSelector(selectSortedCaseDetailSlides);

  // Check Feature Flag for non-Slide Deliverables
  const nonSlideDeliverables = useAppSelector((state) =>
    selectFeatureFlag(state, "nonSlideDeliverables")
  );
  const slidelessViewer = useAppSelector((state) =>
    selectFeatureFlag(state, "slidelessViewer")
  );

  const correctCase =
    !!caseIdParam && !!caseDetailId && caseIdParam === caseDetailId;

  return useMemo(() => {
    if (!correctCase) return [];

    const orphanedSlideViewerItems: ViewerItem[] = sortedSlides
      .filter((slide) => !slide.specimen_id)
      .map((slide) => ({
        id: slide.id || 0,
        type: "slides",
        specimenId: undefined,
      }));

    const viewerItems: ViewerItem[] = sortedSpecimens
      .flatMap((specimen) => {
        const slideItems: ViewerItem[] = sortedSlides
          .filter((slide) => slide.specimen_id === specimen.id)
          .map((slide) => ({
            id: slide.id || 0,
            type: "slides",
            specimenId: specimen.id || 0,
          }));
        const documentItems: ViewerItem[] =
          (nonSlideDeliverables &&
            specimen.case_documents?.map((caseDocument) => ({
              id: caseDocument.id || 0,
              type: "documents",
              specimenId: specimen.id || 0,
            }))) ||
          [];

        if (
          slidelessViewer &&
          slideItems.length < 1 &&
          documentItems.length < 1
        )
          return [
            {
              id: specimen.id || 0,
              type: "specimens",
              specimenId: specimen.id || 0,
            } as ViewerItem,
          ];

        return slideItems.concat(documentItems);
      })
      .filter((viewerItem) => !!viewerItem);

    const results = orphanedSlideViewerItems.concat(viewerItems);

    return results;
  }, [
    correctCase,
    sortedSpecimens,
    sortedSlides,
    nonSlideDeliverables,
    slidelessViewer,
  ]);
};

export default useViewerItems;
